import React, { useCallback, useEffect, useRef, useState, useId } from "react";
import { Loader2, Paperclip, Send, CheckCircle2, AlertTriangle, X, RefreshCw, ShieldCheck } from "lucide-react";
import { API_BASE } from "../utils/api";

/**
 * Attachments: PDF and Word (.docx) only.
 *
 * This used to take images, spreadsheets, plain text and video. On a form that
 * emails whatever it is given straight to a person's inbox, every extra format
 * is another thing arriving at a mail client that will happily open it — and the
 * enquiries this form actually receives are proposals, CVs and papers.
 *
 * Legacy .doc is deliberately not on the list. It is an OLE compound file that
 * can carry a macro stream, and nothing here (or in the Lambda) can tell a clean
 * one from a malicious one without a full parser. Refusing a format we cannot
 * inspect is more honest than waving it through.
 */
const ACCEPTED = [
  {
    ext: ".pdf",
    label: "PDF",
    mime: "application/pdf",
    // "%PDF-"
    magic: [0x25, 0x50, 0x44, 0x46, 0x2d],
  },
  {
    ext: ".docx",
    label: "Word (.docx)",
    mime: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    // Every .docx is a zip: "PK"
    magic: [0x50, 0x4b, 0x03, 0x04],
  },
];
// Matches ATTACH_MAX_BYTES in the Lambda. The old client limit was 10MB against
// a server limit of ~4MB, so a 6MB file passed every check here and then failed
// on send — the one place an error is most expensive.
const MAX_MB = 4;
const MESSAGE_MIN = 5;

/** The first bytes of the file, which is the only part of it that isn't a claim. */
async function readMagic(file, length) {
  const buf = await file.slice(0, length).arrayBuffer();
  return Array.from(new Uint8Array(buf));
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * One field, set like a line on a printed form: a number, a rule, the label,
 * then the answer written on a ruled line beneath.
 *
 * The number is not decoration — it is the same mono-numeral language the rest
 * of the site uses for its sections, and it gives the form a rhythm that a stack
 * of identical grey boxes does not have.
 *
 * Errors sit under their own field rather than collecting in a single banner at
 * the foot of the form, so there is never any doubt which line is wrong, and
 * they are wired with aria-invalid/aria-describedby so a screen reader hears the
 * reason on the field rather than having to hunt for it.
 */
const Field = ({ index, label, error, hint, tone = "light", className = "pt-7", children }) => {
  const ink = tone === "ink";
  const id = useId();
  const errorId = `${id}-error`;
  return (
    <div className={className}>
      <div className="flex items-baseline gap-4 mb-1">
        <span
          className={`font-mono text-[11px] tracking-widest tabular-nums shrink-0 ${
            ink ? "text-[#B8924A]" : "text-[#62706A]"
          }`}
        >
          {String(index).padStart(2, "0")}
        </span>
        <label htmlFor={id} className={`overline cursor-pointer ${ink ? "!text-[#D1CEC7]" : ""}`}>
          {label}
        </label>
        <span aria-hidden="true" className={`h-px flex-1 ${ink ? "bg-[#F9F8F6]/15" : "bg-[#1C2722]/10"}`} />
        {hint && (
          <span className={`overline shrink-0 ${ink ? "!text-[#9AA5A0]" : "!text-[#62706A]"}`}>{hint}</span>
        )}
      </div>
      {children({ id, errorId, invalid: Boolean(error) })}
      {error && (
        <p
          id={errorId}
          className={`mt-2 text-[13px] flex items-center gap-1.5 ${ink ? "text-[#E08A72]" : "text-[#9B3B26]"}`}
        >
          <AlertTriangle size={13} className="shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};

export const ContactForm = ({ variant = "full", tone = "light", testIdPrefix = "contact" }) => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [checkingFile, setCheckingFile] = useState(false);
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  // The arithmetic question, its signed token, and what the visitor typed.
  const [challenge, setChallenge] = useState({ state: "loading", question: "", token: "" });
  const [answer, setAnswer] = useState("");
  const fileRef = useRef(null);
  const formRef = useRef(null);
  // Honeypot. Not React state — nothing should re-render when a bot types in it,
  // and a person never will.
  const honeypotRef = useRef(null);
  // Direct handles on the inputs. Focus management can't go through the DOM
  // looking for [aria-invalid="true"] — that attribute is set by the render that
  // React has not performed yet at the moment validation fails, so the query
  // comes back empty and focus silently stays put.
  const inputRefs = useRef({});
  const registerRef = (key) => (el) => { inputRefs.current[key] = el; };

  /**
   * Ask the server for a question.
   *
   * The answer never travels with it — the token carries a keyed hash the server
   * can check and nobody else can produce — so a bot reading the page learns the
   * question and nothing more. Fetched on mount so the token's issue time starts
   * running while the visitor is still typing, which is also what the server's
   * minimum-fill-time check reads.
   */
  const loadChallenge = useCallback(async (signal) => {
    setChallenge((c) => ({ ...c, state: "loading" }));
    try {
      const res = await fetch(`${API_BASE}/contact/challenge`, { signal });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      if (!data?.question || !data?.token) throw new Error("empty challenge");
      setChallenge({ state: "ready", question: data.question, token: data.token });
      setAnswer("");
    } catch (err) {
      if (err?.name === "AbortError") return;
      setChallenge({ state: "error", question: "", token: "" });
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    loadChallenge(ac.signal);
    return () => ac.abort();
  }, [loadChallenge]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    // Clear a field's error the moment the visitor starts fixing it. Leaving it
    // up until the next submit makes the form feel like it is arguing.
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };

  /**
   * Three checks, in the order that fails cheapest first: the extension, the
   * size, and then the file's own leading bytes.
   *
   * The last one is the point. `accept=".pdf"` only filters the file picker, and
   * `file.type` is whatever the operating system guessed from the same extension
   * we already looked at — so neither says anything about the contents. Reading
   * the first bytes catches `payload.exe` renamed to `report.pdf` here, before
   * the visitor waits through an upload to be told no.
   *
   * The server repeats all of this and then some, because everything on this
   * side can be skipped by not using the page.
   */
  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    const reset = () => { e.target.value = ""; setFile(null); };
    setFileError("");
    if (!f) { setFile(null); return; }

    const kind = ACCEPTED.find((a) => f.name.toLowerCase().endsWith(a.ext));
    if (!kind) {
      setFileError(
        f.name.toLowerCase().endsWith(".doc")
          ? "Older .doc files can't be accepted — they can carry macros that can't be checked. Save it as .docx or PDF."
          : "Only PDF and Word (.docx) files can be attached."
      );
      reset();
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setFileError(`File must be ${MAX_MB}MB or smaller — this one is ${(f.size / (1024 * 1024)).toFixed(1)}MB.`);
      reset();
      return;
    }

    setCheckingFile(true);
    try {
      const head = await readMagic(f, kind.magic.length);
      if (kind.magic.some((byte, i) => head[i] !== byte)) {
        setFileError(`That file is named ${kind.ext} but isn't really a ${kind.label} inside. Please attach the original.`);
        reset();
        return;
      }
    } catch {
      setFileError("That file couldn't be read. Please try attaching it again.");
      reset();
      return;
    } finally {
      setCheckingFile(false);
    }

    setFile(f);
  };

  const clearFile = () => {
    setFile(null);
    setFileError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please tell us your name.";
    if (!form.email.trim()) next.email = "We need an address to reply to.";
    else if (!validateEmail(form.email.trim())) next.email = "That doesn't look like an email address.";
    if (!form.subject.trim()) next.subject = "A short subject helps us route this.";
    if (!form.message.trim()) next.message = "Please write a message.";
    else if (form.message.trim().length < MESSAGE_MIN) next.message = `A little more detail — at least ${MESSAGE_MIN} characters.`;
    if (challenge.state === "ready" && !answer.trim()) next.answer = "Please answer the question.";
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "idle", msg: "" });

    const found = validate();
    if (Object.keys(found).length > 0) {
      setErrors(found);
      // Send focus to the first thing that needs fixing, rather than leaving the
      // visitor to find it — especially on a phone, where the offending field
      // may be off screen. Field order is explicit so "first" means first on the
      // page, not first key enumerated out of an object.
      const firstKey = ["name", "email", "subject", "message", "answer"].find((k) => found[k]);
      const el = inputRefs.current[firstKey];
      el?.focus();
      // Instant, not smooth: smooth scrolling is compositor-driven and does
      // nothing in a page that isn't rendering, and landing on the field matters
      // more than the animation.
      el?.scrollIntoView({ block: "center" });
      return;
    }
    if (fileError) return;
    if (challenge.state !== "ready") {
      setStatus({ state: "error", msg: "The security question hasn't loaded yet. Reload it and try again." });
      return;
    }
    setErrors({});

    setStatus({ state: "loading", msg: "" });
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      fd.append("subject", form.subject.trim());
      fd.append("message", form.message.trim());
      // Tells the recipient which page the enquiry came from — the same form is
      // mounted at the foot of every public page.
      fd.append("page", window.location.pathname);
      fd.append("challenge", challenge.token);
      fd.append("answer", answer.trim());
      // Empty for a person; a bot that fills every input by name gives itself away.
      fd.append("company", honeypotRef.current?.value || "");
      if (file) fd.append("file", file);
      const res = await fetch(`${API_BASE}/contact`, { method: "POST", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Request failed (${res.status})`);
      setStatus({ state: "success", msg: data?.message || "Thank you — your message has been sent." });
      setForm({ name: "", email: "", subject: "", message: "" });
      clearFile();
    } catch (err) {
      setStatus({ state: "error", msg: err?.message || "Something went wrong. Please try again." });
      // A token is single-use in practice: whatever went wrong, the next attempt
      // gets a fresh question rather than replaying one the server may have
      // already aged out.
      loadChallenge();
    }
  };

  const compact = variant === "compact";
  const sending = status.state === "loading";
  const ink = tone === "ink";
  const fieldClass = `field-line ${ink ? "field-line-ink" : ""}`;

  // On success the form is replaced rather than kept below a small confirmation
  // chip. A sent message and an empty form sitting under it invites the visitor
  // to wonder whether it actually went.
  if (status.state === "success") {
    return (
      <div
        role="status"
        className={`border px-6 py-12 sm:px-10 sm:py-16 text-center ${
          ink ? "border-white/20 bg-[#F9F8F6]/[0.04]" : "hairline bg-[#F2EFEA]"
        }`}
        data-testid={`${testIdPrefix}-success`}
      >
        <CheckCircle2 size={28} strokeWidth={1.25} className={`mx-auto ${ink ? "text-[#B8924A]" : "text-[#A17E3B]"}`} />
        <h3 className={`font-serif text-3xl mt-5 ${ink ? "text-[#F9F8F6]" : "text-[#1C2722]"}`}>Message sent.</h3>
        <p className={`mt-3 text-[15px] leading-relaxed max-w-md mx-auto ${ink ? "text-[#D1CEC7]" : "text-[#4A5A52]"}`}>{status.msg}</p>
        <button
          type="button"
          onClick={() => { setStatus({ state: "idle", msg: "" }); loadChallenge(); }}
          className={`!py-2.5 !px-4 mt-8 ${ink ? "inline-flex items-center gap-2 border border-white/40 text-[#F9F8F6] font-mono text-[12px] tracking-[0.18em] uppercase px-[18px] py-[10px] transition-colors hover:bg-[#F9F8F6] hover:text-[#1C2722]" : "btn-outline"}`}
          data-testid={`${testIdPrefix}-write-another`}
        >
          Write another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} ref={formRef} className="w-full relative" data-testid={`${testIdPrefix}-form`} noValidate>
      <div className="grid gap-x-10 md:grid-cols-2">
        <Field index={1} label="Your name" error={errors.name} tone={tone} className="pt-0">
        {({ id, errorId, invalid }) => (
          <input
            id={id}
            ref={registerRef("name")}
            type="text"
            value={form.name}
            onChange={set("name")}
            className={fieldClass}
            autoComplete="name"
            aria-invalid={invalid}
            aria-describedby={invalid ? errorId : undefined}
            data-testid={`${testIdPrefix}-input-name`}
          />
        )}
      </Field>

        <Field index={2} label="Email" error={errors.email} tone={tone} className="pt-7 md:pt-0">
        {({ id, errorId, invalid }) => (
          <input
            id={id}
            ref={registerRef("email")}
            type="email"
            value={form.email}
            onChange={set("email")}
            className={fieldClass}
            autoComplete="email"
            inputMode="email"
            aria-invalid={invalid}
            aria-describedby={invalid ? errorId : undefined}
            data-testid={`${testIdPrefix}-input-email`}
          />
        )}
      </Field>

      </div>

      <Field index={3} label="Subject" error={errors.subject} tone={tone}>
        {({ id, errorId, invalid }) => (
          <input
            id={id}
            ref={registerRef("subject")}
            type="text"
            value={form.subject}
            onChange={set("subject")}
            className={fieldClass}
            placeholder="Collaboration, press, open data, a proposal…"
            aria-invalid={invalid}
            aria-describedby={invalid ? errorId : undefined}
            data-testid={`${testIdPrefix}-input-subject`}
          />
        )}
      </Field>

      <Field
        index={4}
        label="Message"
        error={errors.message}
        hint={form.message.trim().length > 0 ? `${form.message.trim().length} characters` : undefined}
        tone={tone}
      >
        {({ id, errorId, invalid }) => (
          <textarea
            id={id}
            ref={registerRef("message")}
            rows={compact ? 4 : 7}
            value={form.message}
            onChange={set("message")}
            className={fieldClass}
            aria-invalid={invalid}
            aria-describedby={invalid ? errorId : undefined}
            data-testid={`${testIdPrefix}-input-message`}
          />
        )}
      </Field>

      <div className="pt-7">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
          <span className={`font-mono text-[11px] tracking-widest tabular-nums shrink-0 ${ink ? "text-[#B8924A]" : "text-[#62706A]"}`}>05</span>
          <span className={`overline ${ink ? "!text-[#D1CEC7]" : ""}`}>Attachment</span>
          <span aria-hidden="true" className={`h-px flex-1 ${ink ? "bg-[#F9F8F6]/15" : "bg-[#1C2722]/10"}`} />
          <span className={`overline shrink-0 ${ink ? "!text-[#9AA5A0]" : "!text-[#62706A]"}`}>Optional · max {MAX_MB}MB</span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className={`inline-flex items-center gap-2 cursor-pointer border px-4 py-2.5 min-h-[44px] text-[12px] font-mono tracking-widest uppercase transition-colors ${ink ? "border-white/30 text-[#F9F8F6] hover:bg-[#F9F8F6]/10" : "hairline text-[#1C2722] hover:bg-[#E6E4DD]"}`}>
            <Paperclip size={14} />
            <span>{file ? "Change file" : "Attach a file"}</span>
            <input
              ref={fileRef}
              type="file"
              className="sr-only"
              accept={ACCEPTED.map((a) => `${a.ext},${a.mime}`).join(",")}
              onChange={onFileChange}
              data-testid={`${testIdPrefix}-input-file`}
            />
          </label>
          {file && (
            <span
              className={`inline-flex items-center gap-2 min-h-[44px] text-[12px] font-mono border pl-3 pr-1 py-1 ${ink ? "border-white/25 text-[#F9F8F6] bg-[#F9F8F6]/5" : "hairline text-[#1C2722] bg-[#F2EFEA]"}`}
              data-testid={`${testIdPrefix}-file-chip`}
            >
              {file.name} · {(file.size / (1024 * 1024)).toFixed(2)}MB
              <button
                type="button"
                onClick={clearFile}
                aria-label={`Remove ${file.name}`}
                className={`inline-flex items-center justify-center w-11 h-11 shrink-0 transition-colors ${ink ? "text-[#9AA5A0] hover:text-[#F9F8F6]" : "text-[#62706A] hover:text-[#A17E3B]"}`}
              >
                <X size={14} />
              </button>
            </span>
          )}
          {checkingFile && (
            <span
              role="status"
              className={`inline-flex items-center gap-2 text-[12px] font-mono ${ink ? "text-[#9AA5A0]" : "text-[#62706A]"}`}
              data-testid={`${testIdPrefix}-file-checking`}
            >
              <Loader2 size={13} className="animate-spin" /> Checking…
            </span>
          )}
        </div>
        {fileError && (
          <p className="mt-2 text-[13px] text-[#9B3B26] flex items-center gap-1.5" data-testid={`${testIdPrefix}-file-error`}>
            <AlertTriangle size={13} className="shrink-0" /> {fileError}
          </p>
        )}
        {/* Said plainly, and up front. Someone about to attach a paper deserves
            to know what will be accepted before they pick a file, and someone
            attaching a CV deserves to know it will be opened and inspected. */}
        <p className={`mt-3 text-[12.5px] leading-relaxed max-w-lg ${ink ? "text-[#9AA5A0]" : "text-[#62706A]"}`}>
          PDF or Word (<code className="font-mono text-[11.5px]">.docx</code>) only, up to {MAX_MB}MB. Every
          attachment is opened and checked against its own contents before it is sent, so a file
          that isn&apos;t really the type its name claims — or one carrying macros, embedded
          programs or scripts — is refused. Older <code className="font-mono text-[11.5px]">.doc</code> files
          aren&apos;t accepted, because their macros can&apos;t be inspected.
        </p>
      </div>

      {/* 06 — the security question.
          A CAPTCHA would be the obvious thing and the wrong one: it hands a
          third party every visitor's browsing signal, it is a wall in front of
          people using screen readers, and this form receives a handful of
          messages a week. A sum written in words is read by a person in a
          second, and stops the scripted posts that never render the page at
          all. The answer is checked by the server against a signed token, so
          nothing here can be edited around. */}
      <Field
        index={6}
        label="Quick check"
        error={errors.answer}
        tone={tone}
        hint={challenge.state === "ready" ? "Spam check" : undefined}
      >
        {({ id, errorId, invalid }) => (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 pt-1">
            {challenge.state === "loading" && (
              <span
                className={`inline-flex items-center gap-2 text-[15px] ${ink ? "text-[#9AA5A0]" : "text-[#62706A]"}`}
                data-testid={`${testIdPrefix}-challenge-loading`}
              >
                <Loader2 size={15} className="animate-spin" /> Loading the question…
              </span>
            )}

            {challenge.state === "error" && (
              <div className="flex flex-wrap items-center gap-3" data-testid={`${testIdPrefix}-challenge-error`}>
                <span className={`text-[14px] ${ink ? "text-[#E08A72]" : "text-[#9B3B26]"}`}>
                  The question couldn&apos;t be loaded.
                </span>
                <button
                  type="button"
                  onClick={() => loadChallenge()}
                  className={`inline-flex items-center gap-2 border px-3 py-2 text-[11px] font-mono tracking-widest uppercase min-h-[44px] transition-colors ${
                    ink ? "border-white/30 text-[#F9F8F6] hover:bg-[#F9F8F6]/10" : "hairline text-[#1C2722] hover:bg-[#E6E4DD]"
                  }`}
                >
                  <RefreshCw size={13} /> Try again
                </button>
              </div>
            )}

            {challenge.state === "ready" && (
              <>
                <label
                  htmlFor={id}
                  className={`font-serif text-xl leading-none ${ink ? "text-[#F9F8F6]" : "text-[#1C2722]"}`}
                  data-testid={`${testIdPrefix}-challenge-question`}
                >
                  {challenge.question}
                </label>
                <input
                  id={id}
                  ref={registerRef("answer")}
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={answer}
                  onChange={(e) => {
                    setAnswer(e.target.value);
                    setErrors((prev) => (prev.answer ? { ...prev, answer: undefined } : prev));
                  }}
                  className={`${fieldClass} !w-24 text-center tabular-nums`}
                  aria-invalid={invalid}
                  aria-describedby={invalid ? errorId : undefined}
                  data-testid={`${testIdPrefix}-input-answer`}
                />
                <button
                  type="button"
                  onClick={() => loadChallenge()}
                  aria-label="Ask a different question"
                  title="Ask a different question"
                  className={`inline-flex items-center justify-center w-11 h-11 transition-colors ${
                    ink ? "text-[#9AA5A0] hover:text-[#F9F8F6]" : "text-[#62706A] hover:text-[#1C2722]"
                  }`}
                  data-testid={`${testIdPrefix}-challenge-refresh`}
                >
                  <RefreshCw size={15} />
                </button>
              </>
            )}
          </div>
        )}
      </Field>

      {/* Honeypot. Off-screen rather than display:none — a bot that skips hidden
          fields still fills this one — and taken out of the tab order and the
          accessibility tree so nobody using the page ever meets it. */}
      <div aria-hidden="true" className="absolute w-px h-px -m-px overflow-hidden p-0 border-0" style={{ clip: "rect(0 0 0 0)" }}>
        <label htmlFor={`${testIdPrefix}-company`}>Company (leave this blank)</label>
        <input
          ref={honeypotRef}
          id={`${testIdPrefix}-company`}
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          defaultValue=""
        />
      </div>

      <div className={`mt-10 pt-7 border-t flex flex-wrap items-center gap-5 ${ink ? "border-white/15" : "hairline"}`}>
        <button
          type="submit"
          className="btn-terracotta disabled:opacity-60 disabled:cursor-not-allowed"
          disabled={sending}
          data-testid={`${testIdPrefix}-submit-button`}
        >
          {sending ? (
            <><Loader2 className="animate-spin" size={14} /> Sending</>
          ) : (
            <>Send message <Send size={14} /></>
          )}
        </button>

        <p className={`text-[13px] max-w-sm leading-relaxed flex items-start gap-2 ${ink ? "text-[#D1CEC7]" : "text-[#4A5A52]"}`}>
          <ShieldCheck size={15} className={`shrink-0 mt-0.5 ${ink ? "text-[#B8924A]" : "text-[#A17E3B]"}`} />
          <span>
            Goes straight to the group&apos;s inbox. We don&apos;t pass it on to anyone, and
            there is no third-party spam service reading this page.
          </span>
        </p>

        {status.state === "error" && (
          <div
            role="alert"
            className={`w-full flex items-start gap-2 border px-4 py-3 text-[13px] ${ink ? "text-[#E08A72] border-[#E08A72]/50 bg-[#E08A72]/10" : "text-[#9B3B26]"}`}
            style={ink ? undefined : { borderColor: "#9B3B26", background: "#FBEFEA" }}
            data-testid={`${testIdPrefix}-error`}
          >
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            {status.msg}
          </div>
        )}
      </div>
    </form>
  );
};

export default ContactForm;
